import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Chip, Stack, 
  Collapse, IconButton, Alert, Divider
} from '@mui/material';
import {
  Lightbulb as SuggestionIcon,
  TrendingUp as TrendingUpIcon,
  CompareArrows as CompareIcon,
  Analytics as AnalyticsIcon,
  Security as SecurityIcon,
  PlayCircle as PlayIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Bolt as BoltIcon
} from '@mui/icons-material';

import useStudioStore from '../state/studioStore';

const priorityColors = {
  high: 'error',
  medium: 'warning', 
  low: 'info'
};

const toolIcons = {
  diff: <CompareIcon />,
  slice: <AnalyticsIcon />,
  gate: <SecurityIcon />,
  liveTrial: <PlayIcon />,
  ragStudio: <TrendingUpIcon />
};

export default function CrossToolSuggestions() {
  const { 
    workflowContext, 
    generateSuggestions, 
    openToolWithContext,
    contextFeed
  } = useStudioStore();
  
  const [suggestions, setSuggestions] = useState([]);
  const [expanded, setExpanded] = useState(true);

  // Regenerate suggestions when workflow context changes
  useEffect(() => {
    const newSuggestions = generateSuggestions();
    setSuggestions(newSuggestions);
  }, [workflowContext, generateSuggestions]);

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.action.type === 'openTool') {
      openToolWithContext(suggestion.action.tool, suggestion.action.context || {});
    }
  };

  if (suggestions.length === 0) {
    return null;
  }

  const highPriority = suggestions.filter(s => s.priority === 'high');
  const otherSuggestions = suggestions.filter(s => s.priority !== 'high');

  return (
    <Card 
      sx={{ 
        mb: 2,
        background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(156, 39, 176, 0.05) 100%)',
        border: '1px solid rgba(25, 118, 210, 0.2)'
      }}
    >
      <CardContent sx={{ pb: '16px !important' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <BoltIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6">
              Cross-Tool Workflow
            </Typography>
            <Chip 
              label={`${suggestions.length} suggestion${suggestions.length > 1 ? 's' : ''}`}
              size="small"
              variant="outlined"

            />
          </Stack>
          <IconButton 
            onClick={() => setExpanded(!expanded)}
            size="small"
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>

        <Collapse in={expanded}>
          <Stack spacing={2}>
            {/* High Priority Suggestions - Always Visible */}
            {highPriority.map((suggestion) => (
              <Alert
                key={suggestion.id}
                severity="warning"
                action={
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={toolIcons[suggestion.action.tool]}
                    onClick={() => handleSuggestionClick(suggestion)}
                    sx={{ ml: 1 }}
                  >
                    Open {suggestion.action.tool}
                  </Button>
                }
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {suggestion.title}
                </Typography>
                <Typography variant="body2">
                  {suggestion.description}
                </Typography>
              </Alert>
            ))}

            {/* Other Suggestions */}
            {otherSuggestions.map((suggestion, index) => (
              <Box key={suggestion.id}>
                {index > 0 && <Divider sx={{ my: 1 }} />}
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box sx={{ mt: 0.5 }}>
                    {toolIcons[suggestion.action.tool] || <SuggestionIcon />}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {suggestion.title}
                      </Typography>
                      <Chip 
                        label={suggestion.priority}
                        size="small"
                        color={priorityColors[suggestion.priority]}
                        variant="outlined"
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {suggestion.description}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={toolIcons[suggestion.action.tool]}
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      Open {suggestion.action.tool}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            ))}

            {/* Current Context Summary */}
            <Box sx={{ 
              p: 2, 
              bgcolor: 'rgba(0,0,0,0.02)', 
              borderRadius: 1, 
              border: '1px dashed rgba(0,0,0,0.1)' 
            }}>
              <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                Current Workflow Context:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {workflowContext.results && (
                  <Chip 
                    label={`Results (${workflowContext.results.source})`}
                    size="small"
                    variant="outlined"
                  />
                )}
                {workflowContext.comparison && (
                  <Chip 
                    label="Comparison"
                    size="small"
                    variant="outlined"
                  />
                )}
                {workflowContext.sliceAnalysis && (
                  <Chip 
                    label="Slice Analysis"
                    size="small" 
                    variant="outlined"
                  />
                )}
                {(!workflowContext.results && !workflowContext.comparison && !workflowContext.sliceAnalysis) && (
                  <Typography variant="caption" color="text.secondary">
                    No workflow data yet - run a trial to get started
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  );
}